/* eslint-disable react-hooks/exhaustive-deps */
import { bracketOption } from '@/commonFunctions/bracketActions'
import { has } from '@/commonFunctions/utils'
import { Button, ButtonPrimary } from '@/components/button'
import { Checkmark, Close, FilterNoBg } from '@/components/icons'
import { ConfirmModal } from '@/components/modal'
import { Search } from '@/components/search'
import { PlayerItemLoading } from '@/components/skeleton'
import { BRACKET_MODIFY, LIMIT, OFFSET } from '@/constants/Common'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppSelector } from '@/hooks/useRedux'
import { clubSelector } from '@/pages/club/selectors'
import { userSelector } from '@/pages/user/selectors'
import {
  useBracketAddParticipants,
  useBracketSwitchPlayers,
  useBracketWithdraw,
  useGetBracketPlayers,
  useGetClubStaff,
} from '@/services'
import { BracketPlayer, Order, PayloadBracketAddParticipants } from '@/types'
import { Menu, Transition } from '@headlessui/react'
import { isEmpty } from 'lodash'
import { Fragment, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { eventSelector } from '../selectors'
import { AddParticipantModal } from './AddParticipantModal'
import { BracketPlayerItem } from './BracketPlayerItem'
import { SelectBracketModal } from './SelectBracketModal'
import { WithdrawModal } from './WithdrawModal'
import { WithdrawRefundModal } from './WithdrawRefundModal'
type OpenSwitchBracketType = 'STEP1' | 'STEP2' | ''
type BracketPlayerClone = BracketPlayer & { failedReason?: string }

type Props = {
  onReturnPlayersLength: (length: number) => void
}

export const BracketPlayers = ({ onReturnPlayersLength }: Props) => {
  const { t } = useTranslation()

  const params = useParams()
  const { bracket, event } = useAppSelector(eventSelector)
  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const { eventId, bracketId } = params

  const { mutate: addParticipants } = useBracketAddParticipants()
  const { mutate: getClubStaff, data: resGetClubStaff } = useGetClubStaff()
  const { mutate: bracketWithdraw } = useBracketWithdraw()
  const { mutate: switchBrackets } = useBracketSwitchPlayers()

  const [searchText, setSearchText] = useState('')
  const [offset, setOffset] = useState(OFFSET)
  const [players, setPlayers] = useState<Map<number, BracketPlayerClone>>(new Map())
  const [order, setOrder] = useState<Order | ''>('')
  const [filters, setFilters] = useState<{
    isRegistered: boolean
    isWaitListed: boolean
  }>({
    isRegistered: false,
    isWaitListed: false,
  })
  const [addParticipant, setAddParticipant] = useState(false)
  const [switchBracketData, setSwitchBracketData] = useState<any[]>([])
  const [withdrawRefund, setWithdrawRefund] = useState<BracketPlayer | null>(null)
  const [withdraw, setWithdraw] = useState<BracketPlayer | null>(null)
  const [switchBracket, setSwitchBracket] = useState<OpenSwitchBracketType>('')
  const [targetBracket, setTargetBracket] = useState<any>()
  const [availableBrackets, setAvailableBrackets] = useState<any>([])
  const [lastElement, setLastElement] = useState<any>(null)

  const debouncedSearchText = useDebounce<string>(searchText || '*', 200)

  const {
    data: resPlayers,
    isLoading,
    refetch: refetchBracketPlayers,
    isFetching,
  } = useGetBracketPlayers(
    {
      limit: LIMIT,
      offset: offset,
      bracketId: Number(bracketId),
      query: debouncedSearchText || '*',
      filters: filters,
      sort: order
        ? {
            order: order,
            parameter: 'RATINGS',
          }
        : null,
    },
    user?.id
  )

  useEffect(() => {
    if (isFetching) {
      if (offset === OFFSET) {
        setPlayers(new Map())
      }
    }
  }, [isFetching])

  const openWithdraw = (player: BracketPlayer) => setWithdraw(player)

  const closeWithDraw = () => setWithdraw(null)

  const openWithdrawRefund = (player: BracketPlayer) => setWithdrawRefund(player)

  const closeWithdrawRefund = () => setWithdrawRefund(null)

  const openAddParticipantModal = () => setAddParticipant(true)

  const closeAddParticipantModal = () => setAddParticipant(false)

  const openSwitchBracketModal = (step: OpenSwitchBracketType) => setSwitchBracket(step)

  const closeSwitchBracketModal = () => {
    setSwitchBracket('')
    setTargetBracket(null)
  }

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(OFFSET)
    setSearchText(e.target.value)
  }

  const onChangeOrder = (type: Order) => {
    setOffset(OFFSET)
    setOrder(type)
  }

  const onClearFilter = () => {
    if (order === '' && offset === OFFSET && !filters.isRegistered && !filters.isWaitListed) {
      refetchBracketPlayers()
    } else {
      setOffset(OFFSET)
      setOrder('')
      setFilters({
        isRegistered: false,
        isWaitListed: false,
      })
    }
  }

  const handleWithdrawPlayer = () => {
    const payload = {
      bracketId,
      clubId: bracket?.clubId,
      playerId: withdraw?.id,
      ...(withdraw?.registrationDetails?.registrationId && {
        registrationId: withdraw?.registrationDetails?.registrationId,
      }),
    }
    bracketWithdraw(payload, {
      onSuccess: () => {
        closeWithDraw()
        if (offset !== OFFSET) {
          setOffset(OFFSET)
        } else {
          refetchBracketPlayers()
        }
        toast.success('Success')
      },
      onError: () => {
        closeWithDraw()
        toast.error('Error')
      },
    })
  }

  const onConfirmSwitchBracket = () => {
    switchBrackets(switchBracketData, {
      onSuccess: (resBracketSwitchPlayers) => {
        closeSwitchBracketModal()
        setSwitchBracketData([])
        if (resBracketSwitchPlayers) {
          if (resBracketSwitchPlayers.results) {
            if (resBracketSwitchPlayers.results.some((item) => item.isSuccess)) {
              if (offset !== OFFSET) {
                setOffset(OFFSET)
              } else {
                refetchBracketPlayers()
              }
              toast.success(
                `The selected teams were successfully move to the following brackets and matches are reseeded accordingly. : ${switchBracketData?.[0].targetBracketName}`
              )
            } else {
              resBracketSwitchPlayers.results
                .filter((item) => !item.isSuccess)
                .forEach((item) => {
                  toast.error(item.failedReason || APP_CUSTOM_ERROR)
                })
            }
          }
        }
      },
      onError: () => {
        toast.error(APP_CUSTOM_ERROR)
      },
    })
  }

  const onChangeSwitchBracketPlayers = (player: BracketPlayer) => {
    const checkPlayer = switchBracketData.find((item: any) => item.playerId.toString() === player.id.toString()) || []
    if (isEmpty(checkPlayer) && bracket) {
      setSwitchBracketData([
        ...switchBracketData,
        {
          clubId: bracket?.clubId,
          eventId,
          eventName: bracket.leagueName,
          playerId: player.id.toString(),
          reSeedBracket: true,
          sourceBracketId: bracketId,
          sourceBracketName: bracket.name,
          targetBracketId: '',
          targetBracketName: '',
          gender: player.gender,
          age: player.age,
        },
      ])
    } else {
      const switchData = switchBracketData.filter((item: any) => {
        return item.playerId.toString() !== player.id.toString()
      })
      setSwitchBracketData(switchData)
    }
  }

  const onAddParticipants = (value: { playerId?: number; isClubMember?: boolean }) => {
    if (bracket && value.isClubMember !== undefined && value.playerId !== undefined) {
      const payload: PayloadBracketAddParticipants = {
        bracketId: bracket.bracketId.toString(),
        clubId: bracket?.clubId.toString(),
        users: [{ isClubMember: value.isClubMember, userId: value.playerId?.toString() }],
      }
      addParticipants(payload, {
        onSuccess: () => {
          if (offset !== OFFSET) {
            setOffset(OFFSET)
          } else {
            refetchBracketPlayers()
          }
          toast.success('You have successfully added participant in the bracket')
        },
      })
    }
  }

  const refundCallBack = (playerId?: number) => {
    const filteredArr = switchBracketData.filter((item) => item?.playerId?.toString() !== playerId?.toString())
    setSwitchBracketData(filteredArr)
    if (offset === OFFSET) {
      refetchBracketPlayers()
    } else {
      setOffset(OFFSET)
    }
  }

  useEffect(() => {
    if (bracketId) getClubStaff(Number(bracketId))
  }, [bracketId])

  useEffect(() => {
    if (bracket && event) {
      const eligibleBrackets = bracketOption(bracket, switchBracketData, event?.brackets)
      setAvailableBrackets(eligibleBrackets)
    }
  }, [switchBracketData, event, bracket])

  useEffect(() => {
    if (targetBracket) {
      const newData = switchBracketData.map((item: any) => {
        return {
          ...item,
          targetBracketId: targetBracket?.bracketId,
          targetBracketName: targetBracket?.name,
        }
      })
      setSwitchBracketData(newData)
    }
  }, [targetBracket])

  useEffect(() => {
    if (resPlayers) {
      setPlayers((old) => {
        let temp: Map<number, BracketPlayerClone>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        resPlayers.result.hits.forEach((mem) => {
          temp.set(mem.id, mem)
        })

        return temp
      })
      onReturnPlayersLength(resPlayers.result.hits.length)
    }
  }, [isFetching])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <>
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-4">
          <Search searchText={searchText} onChangeInput={onChangeInput} className="py-2 px-3 w-56 mt-3 gap-x-2" />
          <div className="flex gap-x-2 sm:gap-x-4 items-center justify-between sm:justify-start">
            <div className="flex justify-between items-center w-full">
              <div className="flex gap-x-2 sm:gap-x-4 items-center">
                <Menu as="div" className="relative inline-block">
                  <div>
                    <Menu.Button className="flex gap-x-1 items-center bg-primary-500 rounded-full hover:bg-opacity-70 px-3 py-1 text-xs font-semibold text-white">
                      {t('common.sort')}
                      <FilterNoBg className="w-4 h-4" />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute mt-2 w-48 z-50 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="px-1 py-1 ">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                              } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                              onClick={() => onChangeOrder('DESC')}
                            >
                              <Checkmark className={`${order === 'DESC' ? 'opacity-100' : 'opacity-0'}`} />
                              {t('common.highToLowRating')}
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                              } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                              onClick={() => onChangeOrder('ASC')}
                            >
                              <Checkmark className={`${order === 'ASC' ? 'opacity-100' : 'opacity-0'}`} />
                              {t('common.lowToHighRating')}
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>

                <Button
                  className="text-xs font-semibold text-white border border-primary-500 bg-primary-500 bg-opacity-5 px-2 sm:px-4 py-1 flex flex-row gap-x-1 items-center"
                  onClick={() => {
                    setPlayers(new Map())
                    setOffset(OFFSET)
                    setFilters({ ...filters, isRegistered: !filters.isRegistered })
                  }}
                >
                  {filters.isRegistered && <Checkmark className="w-4 h-4 text-white" />}
                  {t('event.registered')}
                </Button>
                <Button
                  className="text-xs font-semibold text-white border border-primary-500 bg-primary-500 bg-opacity-5 px-2 sm:px-4 py-1 flex flex-row gap-x-1 items-center"
                  onClick={() => {
                    setPlayers(new Map())
                    setOffset(OFFSET)
                    setFilters({ ...filters, isWaitListed: !filters.isWaitListed })
                  }}
                >
                  {filters.isWaitListed && <Checkmark className="w-4 h-4 text-white" />}
                  {t('event.waitlisted')}
                </Button>
                <div className="flex items-center">
                  <Button
                    className="text-xs text-white hidden sm:flex font-semibold hover:text-opacity-70"
                    onClick={onClearFilter}
                  >
                    {t('common.clearFilters')}
                  </Button>
                  <Button className="flex sm:hidden" onClick={onClearFilter}>
                    <Close className="text-white hover:text-opacity-70" />
                  </Button>
                </div>
              </div>
              {bracket?.durationStatus === 'COMPLETE' ||
              !has(permissions, BRACKET_MODIFY) ||
              bracket?.hasConfirmMatch ||
              bracket?.isWaitListFull ? (
                has(permissions, BRACKET_MODIFY) &&
                bracket?.format === 'SINGLES' && (
                  <ButtonPrimary
                    title={t('event.switchBracket')}
                    className="!rounded-lg !py-[6px] !px-3"
                    disabled={isEmpty(switchBracketData)}
                    onClick={() => openSwitchBracketModal('STEP1')}
                  />
                )
              ) : (
                <div className="flex gap-x-2 items-center">
                  {has(permissions, BRACKET_MODIFY) && bracket?.format === 'SINGLES' && (
                    <div className="switch-bracket">
                      <ButtonPrimary
                        title={t('event.switch')}
                        className="!rounded-lg !py-[6px] !px-3"
                        disabled={isEmpty(switchBracketData)}
                        onClick={() => openSwitchBracketModal('STEP1')}
                      />
                      <Tooltip anchorSelect=".switch-bracket" place="top">
                        {t('event.switchBracket')}
                      </Tooltip>
                    </div>
                  )}
                  {has(permissions, BRACKET_MODIFY) && bracket?.format === 'SINGLES' ? (
                    <div className="add-participant">
                      <ButtonPrimary
                        title={t('common.add')}
                        className="!rounded-lg !py-[6px] !px-3"
                        onClick={openAddParticipantModal}
                      />
                      <Tooltip anchorSelect=".add-participant" place="top">
                        {t('event.addParticipant')}
                      </Tooltip>
                    </div>
                  ) : (
                    <ButtonPrimary
                      title={t('event.addParticipant')}
                      className="!rounded-lg !py-[6px] !px-3"
                      onClick={openAddParticipantModal}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {Array.from(players.values())?.map((item, index) => {
          const isChecked = switchBracketData.find(
            (switchBracketData: any) => switchBracketData.playerId === item.id.toString()
          )

          return index === players.size - 1 && resPlayers?.result.hasMore ? (
            <div key={item.id} ref={setLastElement}>
              <BracketPlayerItem
                item={item}
                showCheckBox={bracket?.format === 'SINGLES' && has(permissions, BRACKET_MODIFY)}
                isChecked={isChecked}
                onChangeCheckbox={() => {
                  onChangeSwitchBracketPlayers(item)
                }}
                isCheckInput
                isShowMore={
                  has(permissions, BRACKET_MODIFY) &&
                  bracket?.format === 'SINGLES' &&
                  !bracket?.hasConfirmMatch &&
                  bracket?.durationStatus !== 'COMPLETE' &&
                  (bracket?.elimination === 'COMPASS' ? !bracket?.canShowStandings : true)
                }
                organizers={resGetClubStaff?.result.organizers}
                directors={resGetClubStaff?.result.directors}
                onWithdrawRefund={(player, isWithDraw) => {
                  if (player && !isWithDraw) openWithdrawRefund(player)
                  if (player && isWithDraw) openWithdraw(player)
                }}
              />
            </div>
          ) : (
            <BracketPlayerItem
              key={item.id}
              item={item}
              showCheckBox={bracket?.format === 'SINGLES' && has(permissions, BRACKET_MODIFY)}
              isChecked={isChecked}
              onChangeCheckbox={() => {
                onChangeSwitchBracketPlayers(item)
              }}
              isCheckInput
              isShowMore={
                has(permissions, BRACKET_MODIFY) &&
                bracket?.format === 'SINGLES' &&
                !bracket?.hasConfirmMatch &&
                bracket?.durationStatus !== 'COMPLETE' &&
                (bracket?.elimination === 'COMPASS' ? !bracket?.canShowStandings : true)
              }
              organizers={resGetClubStaff?.result.organizers}
              directors={resGetClubStaff?.result.directors}
              onWithdrawRefund={(player, isWithDraw) => {
                if (player && !isWithDraw) openWithdrawRefund(player)
                if (player && isWithDraw) openWithdraw(player)
              }}
            />
          )
        })}

        {isLoading && (
          <>
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
            <PlayerItemLoading className="!max-w-[964px]" />
          </>
        )}

        {resPlayers && !resPlayers?.result.hasMore && (
          <div className="flex justify-center">
            <span className="text-white font-semibold text-opacity-60">{t('common.noMoreResults')}</span>
          </div>
        )}
      </div>
      {bracket && (
        <AddParticipantModal
          open={addParticipant}
          onClose={closeAddParticipantModal}
          onSubmit={(value) => {
            onAddParticipants(value)
          }}
          bracket={bracket}
        />
      )}
      <SelectBracketModal
        open={switchBracket === 'STEP1'}
        onClose={closeSwitchBracketModal}
        title={t('event.switchBrackets')}
        eligibleBrackets={availableBrackets}
        targetBracket={targetBracket}
        setTargetBracket={setTargetBracket}
        onSubmit={() => setSwitchBracket('STEP2')}
        disabledSubmitBtn={!targetBracket}
      />
      <ConfirmModal
        open={switchBracket === 'STEP2'}
        onClose={() => {
          if (switchBracket === 'STEP2') closeSwitchBracketModal()
        }}
        onCloseButton={() => {
          if (switchBracket === 'STEP2') setSwitchBracket('STEP1')
        }}
        title={switchBracket === 'STEP2' ? t('common.confirmation') : ''}
        desc={switchBracket === 'STEP2' ? t('event.switchBracketsConfirmation') : ''}
        onSubmit={() => {
          if (switchBracket === 'STEP2') onConfirmSwitchBracket()
        }}
        cancelTitle={t('common.back')}
        submitTitle={t('common.confirm')}
      />
      {withdrawRefund && bracket && (
        <WithdrawRefundModal
          open={!!withdrawRefund}
          onClose={closeWithdrawRefund}
          currentPlayerId={withdrawRefund?.id}
          currentEventId={eventId}
          currentPlayer={withdrawRefund?.fullName}
          currentClubId={bracket?.clubId}
          refundCallBack={refundCallBack}
        />
      )}
      {withdraw && (
        <WithdrawModal
          title={t('event.withdrawPlayer')}
          open={!!withdraw}
          onClose={closeWithDraw}
          rightButton={handleWithdrawPlayer}
          content={
            <p className="text-sm text-navy-900 text-opacity-70">
              {t('event.confirmWithdrawPlayerFirstText')} <strong>{withdraw?.fullName}</strong>{' '}
              {t('event.confirmWithdrawPlayerSecondText')}
            </p>
          }
        />
      )}
    </>
  )
}
